import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import NavBar from './navbar';
import Ourservices from './ourservices';
import Aboutus from './aboutus';
import Pricing from './pricing';
import RequestDemo from './demorequest';
import FavoriteIcon from '@mui/icons-material/Favorite';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import BoltIcon from '@mui/icons-material/Bolt';
import LockIcon from '@mui/icons-material/Lock';
import TuneIcon from '@mui/icons-material/Tune';
import Constants from '../../constants';
import DownloadIcon from '@mui/icons-material/Download';

const stats = [
  { icon: <WifiTetheringIcon />, label: 'Active Streams', value: '100+' },
  { icon: <DownloadIcon />, label: 'Downloads', value: '6000+' },
  { icon: <CheckCircleOutlineIcon />, label: 'Uptime', value: '99.9%' },
  { icon: <SpeedIcon />, label: 'Latency', value: '< 2s' },
  { icon: <HeadsetMicIcon />, label: 'Support', value: '24/7' },
];

const whyFeatures = [
  {
    icon: <BoltIcon sx={{ fontSize: 28 }} />,
    title: 'Ultra Low Latency',
    desc: 'Near real-time delivery with servers optimized for minimal delay on every stream.',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.1)',
  },
  {
    icon: <LockIcon sx={{ fontSize: 28 }} />,
    title: 'Secure & Reliable',
    desc: 'Enterprise-grade security with 99.9% uptime SLA — your stream never goes dark.',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.1)',
  },
  {
    icon: <TuneIcon sx={{ fontSize: 28 }} />,
    title: 'Fully Customized',
    desc: 'Every plan and server is tailored specifically to your needs and scale requirements.',
    color: '#8b5cf6',
    bg: 'rgba(139, 92, 246, 0.1)',
  },
];

const StatItem = ({ stat, last }) => (
  <Box
    sx={{
      flex: 1,
      textAlign: 'center',
      px: { xs: 2, md: 4 },
      borderRight: last ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
    }}
  >
    <Box sx={{ color: 'white', mb: 0.5, display: 'flex', justifyContent: 'center' }}>
      {stat.icon}
    </Box>
    <Typography
      sx={{
        color: 'white',
        fontWeight: 800,
        fontSize: { xs: '1.6rem', md: '2rem' },
        lineHeight: 1.1,
      }}
    >
      {stat.value}
    </Typography>
    <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
      {stat.label}
    </Typography>
  </Box>
);

const Landing = () => {
  const [enquireOpen, setEnquireOpen] = React.useState(false);
  const openEnquire = React.useCallback(() => setEnquireOpen(true), []);
  const [channels, setChannels] = React.useState([]);

  const openPlaystore = React.useMemo(
    () => () =>
      window.open(
        'https://play.google.com/store/apps/dev?id=6685575201555005039',
        '_blank'
      ),
    []
  );

  React.useEffect(() => {
    async function fetchChannels() {
      fetch("https://api.streamwell.in/api/channel/mobile/mobile")
        .then((response) => {
          if (!response.ok) {
            return [];
          }
          return response.json();
        })
        .then((data) => {
          const channels = data.payload;
          setChannels([...channels, ...channels]);
        })
        .catch((error) => {
        });
    }

    fetchChannels()
  }, [])

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* ── HERO ──────────────────────────────── */}
      <Box
        id="hero"
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <NavBar onContactClick={openEnquire} />

        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 14, md: 18 }, pb: { xs: 6, md: 8 }, flex: 1, position: 'relative', zIndex: 1 }}
        >
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            {/* Left column — text */}
            <Grid item xs={12} md={6}>
              <Chip
                label="Live Streaming Management"
                size="small"
                sx={{
                  background: 'rgba(0,180,216,0.12)',
                  color: '#48cae4',
                  border: '1px solid rgba(0,180,216,0.35)',
                  fontWeight: 700,
                  fontSize: '12px',
                  letterSpacing: '0.6px',
                  mb: 3,
                  height: 30,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  color: '#0d1757',
                  fontSize: { xs: '2.6rem', sm: '3.2rem', md: '4rem' },
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 1.5,
                  letterSpacing: '-0.5px',
                }}
              >
                Stream Without
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.6rem', sm: '3.2rem', md: '4rem' },
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 3,
                  letterSpacing: '-0.5px',
                  color: Constants.secondary_color,
                }}
              >
                Limits.
              </Typography>
              <Typography
                sx={{
                  color: Constants.primary_color,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  lineHeight: 1.75,
                  mb: 4.5,
                  maxWidth: '480px',
                }}
              >
                One platform for RTMP, SRT and HLS streaming with low-latency servers, customized for
                every user. Cloud, Streaming, and Support — all in one place.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4 }}>
                <RequestDemo onButtonClick={openEnquire} showDialog={false} />
                <Box>
                  <Typography
                    sx={{ color: '#6b7280', fontSize: '12px', mb: 1, textTransform: 'none', letterSpacing: '1px' }}
                  >
                    Get featured in our app
                  </Typography>
                  <Button
                    onClick={openPlaystore}
                    sx={{
                      background: 'rgba(5, 15, 102, 0.04)',
                      color: '#0d1757',
                      textTransform: 'none',
                      borderRadius: '10px',
                      px: 2.5,
                      py: 1,
                      border: '1px solid rgba(5, 15, 102, 0.15)',
                      fontSize: '14px',
                      fontWeight: 600,
                      '&:hover': { background: 'rgba(5, 15, 102, 0.08)', border: '1px solid rgba(5, 15, 102, 0.25)' },
                      boxShadow: 'none',
                    }}
                    startIcon={
                      <img
                        alt="Play Store"
                        width="26"
                        height="26"
                        src="playstore.jpeg"
                        style={{ borderRadius: '4px' }}
                      />
                    }
                  >
                    Google Play Store
                  </Button>
                </Box>
              </Stack>

              {/* App store badge */}

            </Grid>

            {/* Right column — image */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: '100%', md: '130%' },
                  maxWidth: { xs: '420px', sm: '520px', md: '720px' },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-16px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '70%',
                    height: '72px',
                    background: 'radial-gradient(ellipse, rgba(0,180,216,0.5) 0%, transparent 70%)',
                    filter: 'blur(18px)',
                  },
                }}
              >
                <img
                  src="landingbg.png"
                  alt="StreamWell RTMP streaming management dashboard"
                  style={{
                    width: '100%',
                    height: 'auto',
                    filter: 'drop-shadow(0 24px 48px rgba(0,120,180,0.35))',
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Stats bar */}
        <Box
          sx={{
            backgroundColor: '#050f66',
            py: { xs: 3, md: 3.5 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                gap: { xs: 3, sm: 0 },
              }}
            >
              {stats.map((stat, i) => (
                <StatItem key={stat.label} stat={stat} last={i === stats.length - 1} />
              ))}
            </Box>
          </Container>
        </Box>
      </Box>



      {/* ── WHY STREAMWELL ──────────────────────── */}
      <Box id="why" sx={{ background: '#ffffff', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              component="span"
              sx={{
                display: 'inline-block',
                background: 'rgba(5, 15, 102, 0.07)',
                color: '#050f66',
                fontWeight: 700,
                fontSize: '13px',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                px: 2,
                py: 0.8,
                borderRadius: '20px',
                mb: 2,
              }}
            >
              Why Streamwell
            </Typography>
            <Typography
              component="h2"
              sx={{
                color: '#0d1757',
                fontSize: { xs: '2rem', md: '2.8rem' },
                fontWeight: 800,
                mb: 2,
                lineHeight: 1.15,
              }}
            >
              Built for Performance
            </Typography>
            <Typography
              sx={{
                color: '#6b7280',
                fontSize: '1.05rem',
                maxWidth: '540px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Everything you need to deliver a flawless streaming experience, every single time.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {whyFeatures.map((feature) => (
              <Grid item xs={12} md={4} key={feature.title}>
                <Box
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '20px',
                    border: '1px solid rgba(5,15,102,0.07)',
                    height: '100%',
                    background: '#ffffff',
                    boxShadow: '0 8px 24px rgba(5, 15, 102, 0.1), 0 2px 8px rgba(5, 15, 102, 0.06)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 20px 56px rgba(5, 15, 102, 0.16), 0 8px 20px rgba(5, 15, 102, 0.1)',
                      transform: 'translateY(-6px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '16px',
                      background: feature.bg,
                      color: feature.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2.5,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0d1757', mb: 1.2 }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: '#6b7280', fontSize: '0.92rem', lineHeight: 1.75 }}>
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── OUR CLIENTS ───────────────────────────── */}
      <Box id="clients"
        sx={{
          py: { xs: 3, md: 3.5 },
          position: 'relative',
          zIndex: 1,
          scrollMarginTop: '80px',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h2"
            sx={{
              color: '#050f66',
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 800,
              mb: 2,
              lineHeight: 1.15,
              textAlign: "center",
              marginBottom: "48px"
            }}
          >
            Our Partners
          </Typography>
          <Box
            sx={{
              display: 'flex',
              width: 'max-content',
              gap: 24,
              animation: 'marquee 25s linear infinite',
              '&:hover': {
                animationPlayState: 'paused', // Pauses the carousel on hover
              },
              // Keyframes for the infinite horizontal scroll
              '@keyframes marquee': {
                '0%': { transform: 'translateX(0%)' },
                '100%': { transform: 'translateX(-50%)' },
              },
            }}
          >
            {channels.map((channel, index) => {
              return <img
                key={`${channel.name}-${index}`}
                component="img"
                src={channel["logo_url"]}
                alt={channel.name}
                height="100px"
                width="100px"
                style={{ borderRadius: "10%" }}
              />
            })}
          </Box>
        </Container>
      </Box>

      {/* ── SERVICES ───────────────────────────── */}
      <Ourservices />

      {/* ── PRICING ───────────────────────────── */}
      <Pricing onContactClick={openEnquire} />


      {/* ── CONTACT ────────────────────────────── */}
      <Aboutus />

      {/* ── CTA BANNER ─────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #020b5c 0%, #0a1882 50%, #050f66 100%)',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(ellipse at 50% 100%, rgba(0,180,216,0.2) 0%, transparent 60%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            component="h2"
            sx={{
              color: 'white',
              fontSize: { xs: '2rem', md: '2.8rem' },
              fontWeight: 800,
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            Ready to Start Streaming?
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '1.05rem',
              mb: 5,
              lineHeight: 1.7,
            }}
          >
            Get your free trial demo today and experience low-latency streaming built for your needs.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <RequestDemo onButtonClick={openEnquire} showDialog={false} />
            <Button
              onClick={openPlaystore}
              variant="outlined"
              size="large"
              sx={{
                color: 'rgba(255,255,255,0.9)',
                borderColor: 'rgba(255,255,255,0.35)',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '12px',
                px: 4,
                py: 1.6,
                '&:hover': {
                  borderColor: 'rgba(255,255,255,0.8)',
                  background: 'rgba(255,255,255,0.07)',
                },
              }}
              startIcon={
                <img alt="Play Store" width="22" height="22" src="playstore.jpeg" style={{ borderRadius: '3px' }} />
              }
            >
              Download App
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ── FOOTER ─────────────────────────────── */}
      <Box
        sx={{
          background: '#010840',
          py: { xs: 5, md: 6 },
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <img src="logo.png" alt="StreamWell RTMP Streaming Platform" width="140" height="40" style={{ opacity: 0.88 }} />
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '13px',
                  mt: 1.5,
                  maxWidth: '300px',
                  lineHeight: 1.7,
                }}
              >
                Cloud, Streaming &amp; Support —{' '}
                RTMP Stream Management Service
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                divider={
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: 'rgba(255,255,255,0.1)', display: { xs: 'none', sm: 'block' } }}
                  />
                }
                spacing={2}
                sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}
              >
                {['Solutions', 'Pricing', 'Contact', 'FAQs', 'Privacy'].map((link) => (
                  <Typography
                    key={link}
                    component="span"
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      '&:hover': { color: 'rgba(255,255,255,0.85)' },
                      transition: 'color 0.2s',
                    }}
                  >
                    {link}
                  </Typography>
                ))}
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 4 }} />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems="center"
            spacing={1}
          >
            <Stack direction="row" alignItems="center" spacing={0.8}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                Made with
              </Typography>
              <FavoriteIcon sx={{ color: '#ef4444', fontSize: 16 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>
                by #Streamwell
              </Typography>
            </Stack>
            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>
              © 2026 StreamWell — All Rights Reserved
            </Typography>
          </Stack>
        </Container>
      </Box>

      <RequestDemo open={enquireOpen} onOpenChange={setEnquireOpen} showButton={false} />
    </Box>
  );
};

export default Landing;
